<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Icon from "@components/common/Icon.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { openToast } from "@utils/openToast.js";
	import type { HTMLAttributes } from "svelte/elements";
	import minusIcon from "@core/assets/icons/minus.svg?raw";
	import plusIcon from "@core/assets/icons/plus.svg?raw";
	import thumbsUpIcon from "@core/assets/icons/thumbs-up.svg?raw";
	import trashIcon from "@core/assets/icons/trash-can.svg?raw";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { OrderProductAttribute } from "@core/schema/OrderProductInfo.js";
	import type { ProductOfVariantForCustomer } from "@core/schema/Product.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { isSameOrderProduct } from "@core/schema/utils/isSameOrderProduct.js";
	import { transformToOrderProduct } from "@core/schema/utils/transformToOrderProduct.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";
	import translations from "./ProductBasketButtons.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	const {
		product,
		attributes,
		maxQuantity,
		...restProps
	}: {
		maxQuantity: number;
		product: ProductOfVariantForCustomer;
		attributes: OrderProductAttribute[];
	} & HTMLAttributes<HTMLDivElement> = $props();

	const { subcategories, tenant } = getGlobalTemplateProps();

	const newOrderProduct = $derived(
		Cart.currency !== undefined && selectedDestinationCountry.current !== null
			? transformToOrderProduct(
					product,
					attributes,
					{
						destinationCountry: selectedDestinationCountry.current,
						currency: Cart.currency,
						subcategories,
						vatType: Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
					},
					1,
				)
			: undefined,
	);
	const orderProduct = $derived(
		newOrderProduct !== undefined
			? Cart.orderProducts.find((product) => isSameOrderProduct(product, newOrderProduct))
			: undefined,
	);
	const countInBasket = $derived(orderProduct?.quantity ?? 0);

	function addToCart(): void {
		Cart.addProduct(product, attributes, 1, undefined, (newlyAdded) => {
			openToast({
				group: "cart",
				icon: thumbsUpIcon,
				title: translate(newlyAdded ? "addedToCart" : "againAdded", { name: product.name[tenant.locale] }),
				onclick(): void {
					location.assign(getCartPath(tenant));
				},
			});
		});
	}
</script>

<div class={[restProps.class]}>
	{#if orderProduct}
		<div
			class={[
				"h-[4.5rem]",
				"bg-fcrdarkgray enabled:hover:bg-fcrbrown-100 text-white",
				"px-3 w-full flex items-center justify-between",
				"uppercase font-semibold tracking-[0.016em] rounded-full",
			]}
		>
			<button
				class={[
					"size-12 bg-fcrdarkgray text-white rounded-full border border-white/16 flex justify-center items-center",
					"[&:not(:disabled):hover:active]:scale-95 transition duration-150 ease-in-out",
					"[&:not(:disabled):hover]:border-white/40",
				]}
				aria-label={translate(countInBasket > 0 ? "removeOneItem" : "removeItem")}
				onclick={(): void => {
					Cart.removeProduct(orderProduct, 1);
				}}
			>
				<Icon icon={countInBasket > 1 ? minusIcon : trashIcon} class="size-6" />
			</button>
			<output aria-live="polite" class="text-4 font-semibold">{countInBasket}</output>
			<button
				disabled={countInBasket >= maxQuantity}
				class={[
					"size-12 bg-white text-fcrdarkgray rounded-full flex justify-center items-center",
					"[&:not(:disabled):hover:active]:scale-95 transition duration-150 ease-in-out",
					countInBasket >= maxQuantity && "cursor-not-allowed",
				]}
				aria-label={countInBasket >= maxQuantity ? translate("maximumReached") : translate("addAnother")}
				onclick={addToCart}
			>
				<Icon icon={plusIcon} class="size-6" />
			</button>
		</div>
	{:else}
		<Button
			disabled={maxQuantity === 0}
			size={ButtonSize.Large}
			variant={ButtonVariant.Dark}
			class="w-full"
			onclick={addToCart}
		>
			<div class="text-4 font-semibold">
				{#if maxQuantity === 0}
					{translate("soldOut")}
				{:else}
					{translate("addToBasket")}
				{/if}
			</div>
		</Button>
	{/if}
</div>
